import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import api from '@/utils/api';
import { toast } from 'sonner';
import {
  LogOut, CheckCircle, XCircle, Users, Shield, BarChart3,
  UserPlus, Clock, Search, Briefcase, GraduationCap, Eye, Trash2,
  FolderOpen, Building2, Radar, Activity, FileText, KeyRound, History, TrendingUp,
  DollarSign, TrendingDown, Wallet, Receipt, MessageSquare, Plus
} from 'lucide-react';
import MessagesNavButton from '@/components/MessagesNavButton';
import XVAssistWidget from '@/components/XVAssistWidget';
import AdminOverviewSection from '@/components/AdminOverviewSection';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line, RadarChart, PolarGrid, PolarAngleAxis,
  PolarRadiusAxis, Radar as RechartsRadar, Legend
} from 'recharts';

const CHART_COLORS = ['#22c55e', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#ec4899', '#f97316', '#14b8a6'];

const ADMIN_ROLE_LABELS = {
  content_moderator: "Content Moderator",
  scout_approvals: "Scout Approvals Manager",
  school_support: "School & Club Support",
  data_manager: "Data Manager",
  communications: "Communications Manager",
  full_admin: "Full Administrator",
};

function StatCard({ icon: Icon, label, value, color = 'text-primary-neon', bgColor = 'bg-primary-neon/10', sub }) {
  return (
    <Card className="bg-zinc-900 border-zinc-800 p-5">
      <div className="flex items-center gap-3">
        <div className={`w-11 h-11 ${bgColor} rounded-lg flex items-center justify-center flex-shrink-0`}>
          <Icon className={`w-5 h-5 ${color}`} />
        </div>
        <div className="min-w-0">
          <p className="text-zinc-400 text-xs truncate">{label}</p>
          <p className="text-xl font-heading font-bold text-white">{value}</p>
          {sub && <p className="text-zinc-500 text-xs">{sub}</p>}
        </div>
      </div>
    </Card>
  );
}

function ApplicationRow({ user: u, onApprove, onReject }) {
  const roleColors = { player: 'bg-green-500/10 text-green-400', coach: 'bg-blue-500/10 text-blue-400', school_club: 'bg-orange-500/10 text-orange-400', scout: 'bg-purple-500/10 text-purple-400' };
  const statusColors = { pending: 'bg-yellow-500/10 text-yellow-400', approved: 'bg-green-500/10 text-green-400', rejected: 'bg-red-500/10 text-red-400' };
  return (
    <tr className="hover:bg-zinc-800/50 border-b border-zinc-800" data-testid={`application-row-${u.id}`}>
      <td className="px-4 py-3 text-sm text-white">{u.full_name}</td>
      <td className="px-4 py-3 text-sm text-zinc-400">{u.email}</td>
      <td className="px-4 py-3"><Badge className={`border-0 ${roleColors[u.role] || 'bg-zinc-500/10 text-zinc-400'}`}>{u.role === 'school_club' ? 'School/Club' : u.role}</Badge></td>
      <td className="px-4 py-3 text-sm text-zinc-400 capitalize">{u.gender || '-'}</td>
      <td className="px-4 py-3"><Badge className={`border-0 ${statusColors[u.status] || ''}`}>{u.status}</Badge></td>
      <td className="px-4 py-3 text-sm text-zinc-500">{new Date(u.created_at).toLocaleDateString()}</td>
      <td className="px-4 py-3">{u.status === 'pending' && (
        <div className="flex gap-1.5">
          <Button size="sm" onClick={() => onApprove(u.id)} className="bg-green-600 hover:bg-green-700 text-white h-7 px-2.5 text-xs" data-testid={`approve-btn-${u.id}`}><CheckCircle className="w-3 h-3 mr-1" />Approve</Button>
          <Button size="sm" variant="ghost" onClick={() => onReject(u.id)} className="text-red-400 hover:text-red-300 h-7 px-2.5 text-xs" data-testid={`reject-btn-${u.id}`}><XCircle className="w-3 h-3 mr-1" />Reject</Button>
        </div>
      )}</td>
    </tr>
  );
}

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const isSuperAdmin = user?.role === 'superadmin';

  const [overview, setOverview] = useState(null);
  const [applications, setApplications] = useState([]);
  const [verifications, setVerifications] = useState([]);
  const [allAdmins, setAllAdmins] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [categoryPlayers, setCategoryPlayers] = useState([]);
  const [categoryInstitutions, setCategoryInstitutions] = useState([]);
  const [analyticsGrowth, setAnalyticsGrowth] = useState(null);
  const [analyticsContent, setAnalyticsContent] = useState(null);
  const [analyticsScout, setAnalyticsScout] = useState(null);
  const [analyticsMessaging, setAnalyticsMessaging] = useState(null);
  const [financeOverview, setFinanceOverview] = useState(null);
  const [financeTxns, setFinanceTxns] = useState([]);
  const [showCreateTxn, setShowCreateTxn] = useState(false);
  const [newTxn, setNewTxn] = useState({ amount: '', type: 'revenue', category: 'subscription', description: '', recurring: false });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateAdmin, setShowCreateAdmin] = useState(false);
  const [newAdmin, setNewAdmin] = useState({ email: '', password: '', full_name: '', admin_role: 'full_admin' });
  const [creatingAdmin, setCreatingAdmin] = useState(false);
  const [playerSearch, setPlayerSearch] = useState('');
  const [catFilter, setCatFilter] = useState({ province: '', position: '', verified: '', gender: '' });

  const fetchData = useCallback(async () => {
    try {
      const calls = [
        api.get('/admin/applications'),
        api.get('/admin/verifications'),
        api.get('/admin/users')
      ];
      if (isSuperAdmin) {
        calls.push(
          api.get('/superadmin/overview'),
          api.get('/superadmin/admins'),
          api.get('/superadmin/audit-log?limit=50'),
          api.get('/superadmin/analytics/growth'),
          api.get('/superadmin/analytics/content'),
          api.get('/superadmin/analytics/scout-activity'),
          api.get('/superadmin/analytics/messaging'),
          api.get('/superadmin/finance/overview'),
          api.get('/superadmin/finance/transactions?limit=100')
        );
      } else {
        calls.push(api.get('/admin/stats'));
      }
      const results = await Promise.allSettled(calls);
      if (results[0].status === 'fulfilled') setApplications(results[0].value.data);
      if (results[1].status === 'fulfilled') setVerifications(results[1].value.data);
      if (results[2].status === 'fulfilled') {
        const adminUsers = results[2].value.data.filter(u => u.role === 'admin' || u.role === 'superadmin');
        setAllAdmins(adminUsers);
      }
      if (isSuperAdmin) {
        if (results[3]?.status === 'fulfilled') setOverview(results[3].value.data);
        if (results[4]?.status === 'fulfilled') setAllAdmins(results[4].value.data);
        if (results[5]?.status === 'fulfilled') setAuditLogs(results[5].value.data);
        if (results[6]?.status === 'fulfilled') setAnalyticsGrowth(results[6].value.data);
        if (results[7]?.status === 'fulfilled') setAnalyticsContent(results[7].value.data);
        if (results[8]?.status === 'fulfilled') setAnalyticsScout(results[8].value.data);
        if (results[9]?.status === 'fulfilled') setAnalyticsMessaging(results[9].value.data);
        if (results[10]?.status === 'fulfilled') setFinanceOverview(results[10].value.data);
        if (results[11]?.status === 'fulfilled') setFinanceTxns(results[11].value.data);
      } else {
        if (results[3]?.status === 'fulfilled') {
          const s = results[3].value.data;
          setOverview({ users: { total: s.total_users, players: s.players, coaches: s.coaches, schools_clubs: s.schools_clubs, scouts: s.scouts, admins: s.admins, pending: s.pending_applications, approved: s.approved_users, rejected: s.rejected_users }, profiles: { verified: 0, unverified: 0 }, activity: { pending_verifications: s.pending_verifications }, charts: {} });
        }
      }
    } catch (e) { console.error('Failed to load admin dashboard data', e); } finally { setLoading(false); }
  }, [isSuperAdmin]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const fetchCategoryPlayers = async () => {
    try {
      const params = new URLSearchParams();
      if (playerSearch) params.append('search', playerSearch);
      if (catFilter.province) params.append('province', catFilter.province);
      if (catFilter.position) params.append('position', catFilter.position);
      if (catFilter.verified) params.append('verified', catFilter.verified);
      if (catFilter.gender) params.append('gender', catFilter.gender);
      const res = await api.get(`/superadmin/players?${params}`);
      setCategoryPlayers(res.data);
    } catch { toast.error('Failed to load players'); }
  };

  const fetchCategoryInstitutions = async (type) => {
    try {
      const params = type ? `?inst_type=${type}` : '';
      const res = await api.get(`/superadmin/institutions${params}`);
      setCategoryInstitutions(res.data);
    } catch { toast.error('Failed to load institutions'); }
  };

  const handleApproveApp = async (uid) => { try { await api.post(`/admin/applications/${uid}/approve`); toast.success('Approved'); fetchData(); } catch { toast.error('Failed'); } };
  const handleRejectApp = async (uid) => { try { await api.post(`/admin/applications/${uid}/reject`); toast.success('Rejected'); fetchData(); } catch { toast.error('Failed'); } };
  const handleApproveVerification = async (vid) => { try { await api.post(`/admin/verifications/${vid}/approve`); toast.success('Approved'); fetchData(); } catch { toast.error('Failed'); } };
  const handleRejectVerification = async (vid) => { try { await api.post(`/admin/verifications/${vid}/reject`); toast.success('Rejected'); fetchData(); } catch { toast.error('Failed'); } };

  const handleCreateAdmin = async (e) => {
    e.preventDefault();
    setCreatingAdmin(true);
    try {
      await api.post('/admin/create-admin', newAdmin);
      toast.success('Admin created'); setNewAdmin({ email: '', password: '', full_name: '', admin_role: 'full_admin' }); setShowCreateAdmin(false); fetchData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed'); } finally { setCreatingAdmin(false); }
  };

  const handleDeleteAdmin = async (adminId) => {
    try { await api.delete(`/superadmin/admins/${adminId}`); toast.success('Admin deleted'); fetchData(); } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleGeneratePassword = async () => {
    try { const res = await api.post('/superadmin/passwords/generate'); setNewAdmin(prev => ({ ...prev, password: res.data.password })); toast.success('Password generated'); } catch { toast.error('Failed'); }
  };

  const handleCreateTransaction = async (e) => {
    e.preventDefault();
    try {
      const params = new URLSearchParams({
        amount: newTxn.amount,
        type: newTxn.type,
        category: newTxn.category,
        description: newTxn.description,
        recurring: newTxn.recurring.toString()
      });
      await api.post(`/superadmin/finance/transactions?${params}`);
      toast.success('Transaction recorded');
      setNewTxn({ amount: '', type: 'revenue', category: 'subscription', description: '', recurring: false });
      setShowCreateTxn(false);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to record'); }
  };

  const handleDeleteTransaction = async (txnId) => {
    try {
      await api.delete(`/superadmin/finance/transactions/${txnId}`);
      toast.success('Transaction deleted');
      fetchData();
    } catch { toast.error('Failed to delete'); }
  };

  const filteredApplications = applications.filter(app => {
    const matchesFilter = filter === 'all' || app.status === filter;
    const matchesSearch = !searchTerm || app.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) || app.email?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  if (loading) return <div className="min-h-screen bg-zinc-950 flex items-center justify-center"><div className="text-primary-neon text-xl">Loading dashboard...</div></div>;

  const SA_PROVINCES = ["Eastern Cape","Western Cape","Gauteng","KwaZulu-Natal","Limpopo","Mpumalanga","North West","Free State","Northern Cape"];

  const roleChartData = overview?.charts?.by_role ? Object.entries(overview.charts.by_role).map(([k, v]) => ({ name: k.replace('_', '/'), value: v })) : [];
  const provinceChartData = overview?.charts?.by_province ? Object.entries(overview.charts.by_province).map(([k, v]) => ({ province: k, count: v })) : [];
  const positionChartData = overview?.charts?.by_position || [];
  const ageChartData = overview?.charts?.by_age || [];

  return (
    <div className="min-h-screen bg-zinc-950 text-white" data-testid="admin-dashboard">
      <nav className="border-b border-zinc-800 bg-zinc-900/50" data-testid="admin-nav">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-3">
              <img src="/logo.png" alt="FutureXV" className="h-10" />
              <span className="text-zinc-600">|</span>
              <span className="font-heading font-bold">{isSuperAdmin ? 'SuperAdmin' : 'Admin'}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-zinc-400 text-sm hidden sm:block">{user?.email}</span>
              <Badge className={isSuperAdmin ? 'bg-yellow-500/10 text-yellow-400 border border-yellow-500/20' : 'bg-blue-500/10 text-blue-400 border border-blue-500/20'}>{isSuperAdmin ? 'SuperAdmin' : 'Admin'}</Badge>
              <MessagesNavButton />
              <Button onClick={() => { logout(); navigate('/'); }} variant="ghost" className="text-zinc-400 hover:text-white" data-testid="admin-logout-button"><LogOut className="w-4 h-4 mr-2" />Logout</Button>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Live Overview Panel */}
        {overview && (
          <div className="mb-8" data-testid="overview-panel">
            <h2 className="text-2xl font-heading font-bold mb-4 flex items-center gap-2"><Activity className="w-5 h-5 text-primary-neon" />Platform Overview</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
              <StatCard icon={Clock} label="Pending Approvals" value={overview.users.pending} color="text-yellow-400" bgColor="bg-yellow-500/10" />
              <StatCard icon={Users} label="Total Users" value={overview.users.total} />
              <StatCard icon={Users} label="Players" value={overview.users.players} color="text-green-400" bgColor="bg-green-500/10" />
              <StatCard icon={Briefcase} label="Coaches" value={overview.users.coaches} color="text-blue-400" bgColor="bg-blue-500/10" />
              <StatCard icon={Building2} label="Schools/Clubs" value={overview.users.schools_clubs} color="text-orange-400" bgColor="bg-orange-500/10" />
              <StatCard icon={Eye} label="Scouts" value={overview.users.scouts} color="text-purple-400" bgColor="bg-purple-500/10" />
            </div>
            {overview.profiles && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
                <StatCard icon={Shield} label="Verified Profiles" value={overview.profiles.verified} color="text-blue-400" bgColor="bg-blue-500/10" />
                <StatCard icon={Shield} label="Unverified" value={overview.profiles.unverified} color="text-zinc-400" bgColor="bg-zinc-700/30" />
                <StatCard icon={Activity} label="Pending Verifications" value={overview.activity?.pending_verifications || 0} color="text-yellow-400" bgColor="bg-yellow-500/10" />
                <StatCard icon={TrendingUp} label="Recent (7d)" value={overview.users.recent_registrations || 0} color="text-emerald-400" bgColor="bg-emerald-500/10" />
              </div>
            )}
          </div>
        )}

        <AdminOverviewSection />

        <Tabs defaultValue="applications" className="w-full">
          <TabsList className={`grid w-full mb-6 bg-zinc-900 ${isSuperAdmin ? 'grid-cols-7 max-w-4xl' : 'grid-cols-3 max-w-3xl'}`} data-testid="admin-tabs">
            <TabsTrigger value="applications" className="data-[state=active]:bg-primary-neon data-[state=active]:text-black text-xs" data-testid="applications-tab"><Clock className="w-3.5 h-3.5 mr-1" />Applications</TabsTrigger>
            <TabsTrigger value="verifications" className="data-[state=active]:bg-primary-neon data-[state=active]:text-black text-xs" data-testid="verifications-tab"><Shield className="w-3.5 h-3.5 mr-1" />Verifications</TabsTrigger>
            {isSuperAdmin && <TabsTrigger value="categories" className="data-[state=active]:bg-primary-neon data-[state=active]:text-black text-xs" data-testid="categories-tab"><FolderOpen className="w-3.5 h-3.5 mr-1" />Categories</TabsTrigger>}
            {isSuperAdmin && <TabsTrigger value="charts" className="data-[state=active]:bg-primary-neon data-[state=active]:text-black text-xs" data-testid="charts-tab"><BarChart3 className="w-3.5 h-3.5 mr-1" />Analytics</TabsTrigger>}
            {isSuperAdmin && <TabsTrigger value="finance" className="data-[state=active]:bg-primary-neon data-[state=active]:text-black text-xs" data-testid="finance-tab"><Wallet className="w-3.5 h-3.5 mr-1" />Finance</TabsTrigger>}
            {isSuperAdmin && <TabsTrigger value="manage" className="data-[state=active]:bg-primary-neon data-[state=active]:text-black text-xs" data-testid="manage-tab"><UserPlus className="w-3.5 h-3.5 mr-1" />Admins</TabsTrigger>}
            {isSuperAdmin && <TabsTrigger value="audit" className="data-[state=active]:bg-primary-neon data-[state=active]:text-black text-xs" data-testid="audit-tab"><History className="w-3.5 h-3.5 mr-1" />Audit Log</TabsTrigger>}
          </TabsList>

          {/* APPLICATIONS TAB */}
          <TabsContent value="applications" data-testid="applications-content">
            <div className="flex flex-col sm:flex-row gap-3 mb-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                <Input placeholder="Search..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10 bg-zinc-900/50 border-zinc-800 text-white h-9" data-testid="search-applications-input" />
              </div>
              <div className="flex gap-1.5">
                {['pending','approved','rejected','all'].map(f => (
                  <Button key={f} variant={filter===f?'default':'outline'} size="sm" onClick={() => setFilter(f)}
                    className={`h-9 ${filter===f?'bg-primary-neon text-black hover:bg-primary-neon/90':'border-zinc-700 text-zinc-400 hover:text-white text-xs'}`} data-testid={`filter-${f}-button`}>
                    {f.charAt(0).toUpperCase()+f.slice(1)}{f==='pending'&&overview?` (${overview.users.pending})`:''}
                  </Button>
                ))}
              </div>
            </div>
            <Card className="bg-zinc-900 border-zinc-800 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="applications-table">
                  <thead className="bg-zinc-950 border-b border-zinc-800">
                    <tr>
                      {['Name','Email','Role','Gender','Status','Date','Actions'].map(h => (
                        <th key={h} className="px-4 py-3 text-left text-xs font-heading font-bold text-zinc-400 uppercase">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredApplications.length === 0 ? (<tr><td colSpan={7} className="px-4 py-10 text-center text-zinc-500">No applications found</td></tr>) :
                      filteredApplications.map(app => <ApplicationRow key={app.id} user={app} onApprove={handleApproveApp} onReject={handleRejectApp} />)
                    }
                  </tbody>
                </table>
              </div>
            </Card>
          </TabsContent>

          {/* VERIFICATIONS TAB */}
          <TabsContent value="verifications" data-testid="verifications-content">
            {verifications.length === 0 ? (
              <Card className="bg-zinc-900 border-zinc-800 p-12 text-center"><Shield className="w-12 h-12 mx-auto mb-4 text-zinc-700" /><p className="text-zinc-500">No pending verification requests</p></Card>
            ) : (
              <div className="space-y-3">
                {verifications.map(v => (
                  <Card key={v.id} className="bg-zinc-900 border-zinc-800 p-5" data-testid={`verification-${v.id}`}>
                    <div className="flex justify-between items-center">
                      <div>
                        <h3 className="text-lg font-heading font-bold">{v.player_name}</h3>
                        <p className="text-zinc-400 text-sm">Coach: {v.coach_name} ({v.coach_email}) &middot; {v.school_club}</p>
                      </div>
                      <div className="flex gap-2">
                        <Button onClick={() => handleApproveVerification(v.id)} size="sm" className="bg-green-600 hover:bg-green-700 text-white" data-testid={`approve-verification-${v.id}`}><CheckCircle className="w-3 h-3 mr-1" />Approve</Button>
                        <Button onClick={() => handleRejectVerification(v.id)} size="sm" variant="ghost" className="text-red-400 hover:text-red-300" data-testid={`reject-verification-${v.id}`}><XCircle className="w-3 h-3 mr-1" />Reject</Button>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* CATEGORIES TAB (SuperAdmin) */}
          {isSuperAdmin && (
            <TabsContent value="categories" data-testid="categories-content">
              <Tabs defaultValue="players" className="w-full">
                <TabsList className="grid w-full grid-cols-3 mb-6 bg-zinc-900 max-w-md">
                  <TabsTrigger value="players" onClick={() => fetchCategoryPlayers()} className="data-[state=active]:bg-primary-neon data-[state=active]:text-black text-xs">Players</TabsTrigger>
                  <TabsTrigger value="schools" onClick={() => fetchCategoryInstitutions('school')} className="data-[state=active]:bg-primary-neon data-[state=active]:text-black text-xs">Schools</TabsTrigger>
                  <TabsTrigger value="clubs" onClick={() => fetchCategoryInstitutions('club')} className="data-[state=active]:bg-primary-neon data-[state=active]:text-black text-xs">Clubs</TabsTrigger>
                </TabsList>

                <TabsContent value="players">
                  <div className="flex flex-wrap gap-2 mb-4">
                    <Input placeholder="Search name/email..." value={playerSearch} onChange={(e) => setPlayerSearch(e.target.value)} className="bg-zinc-900 border-zinc-800 text-white h-9 w-48" data-testid="cat-player-search" />
                    <Select value={catFilter.province || 'all'} onValueChange={v => setCatFilter({...catFilter, province: v === 'all' ? '' : v})}>
                      <SelectTrigger className="bg-zinc-900 border-zinc-800 text-white h-9 w-40"><SelectValue placeholder="Province" /></SelectTrigger>
                      <SelectContent className="bg-zinc-900 border-zinc-800 text-white"><SelectItem value="all">All Provinces</SelectItem>{SA_PROVINCES.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                    </Select>
                    <Select value={catFilter.gender || 'all'} onValueChange={v => setCatFilter({...catFilter, gender: v === 'all' ? '' : v})}>
                      <SelectTrigger className="bg-zinc-900 border-zinc-800 text-white h-9 w-32"><SelectValue placeholder="Gender" /></SelectTrigger>
                      <SelectContent className="bg-zinc-900 border-zinc-800 text-white"><SelectItem value="all">Any</SelectItem><SelectItem value="male">Male</SelectItem><SelectItem value="female">Female</SelectItem></SelectContent>
                    </Select>
                    <Select value={catFilter.verified || 'all'} onValueChange={v => setCatFilter({...catFilter, verified: v === 'all' ? '' : v})}>
                      <SelectTrigger className="bg-zinc-900 border-zinc-800 text-white h-9 w-32"><SelectValue placeholder="Verified" /></SelectTrigger>
                      <SelectContent className="bg-zinc-900 border-zinc-800 text-white"><SelectItem value="all">Any</SelectItem><SelectItem value="true">Verified</SelectItem><SelectItem value="false">Unverified</SelectItem></SelectContent>
                    </Select>
                    <Button onClick={fetchCategoryPlayers} className="btn-primary h-9" data-testid="cat-player-search-btn"><Search className="w-3.5 h-3.5 mr-1" />Filter</Button>
                  </div>
                  <Card className="bg-zinc-900 border-zinc-800 overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="w-full" data-testid="cat-players-table">
                        <thead className="bg-zinc-950 border-b border-zinc-800">
                          <tr>{['Name','Email','Position','Age','Gender','Province','School/Club','Verified','Videos','Status'].map(h => <th key={h} className="px-4 py-3 text-left text-xs font-heading font-bold text-zinc-400 uppercase">{h}</th>)}</tr>
                        </thead>
                        <tbody>
                          {categoryPlayers.length === 0 ? <tr><td colSpan={10} className="px-4 py-10 text-center text-zinc-500">Click "Filter" to load players</td></tr> :
                            categoryPlayers.map(item => (
                              <tr key={item.profile?.id} className="border-b border-zinc-800 hover:bg-zinc-800/30">
                                <td className="px-4 py-3 text-sm text-white">{item.user?.full_name}</td>
                                <td className="px-4 py-3 text-sm text-zinc-400">{item.user?.email}</td>
                                <td className="px-4 py-3 text-sm">{item.profile?.position}</td>
                                <td className="px-4 py-3 text-sm text-zinc-400">{item.profile?.age}</td>
                                <td className="px-4 py-3 text-sm text-zinc-400 capitalize">{item.profile?.gender}</td>
                                <td className="px-4 py-3 text-sm text-zinc-400">{item.profile?.province}</td>
                                <td className="px-4 py-3 text-sm text-zinc-400">{item.profile?.school_club}</td>
                                <td className="px-4 py-3">{item.profile?.verified ? <Badge className="bg-green-500/10 text-green-400 border-0">Yes</Badge> : <Badge className="bg-zinc-500/10 text-zinc-400 border-0">No</Badge>}</td>
                                <td className="px-4 py-3 text-sm text-zinc-400">{item.video_count}</td>
                                <td className="px-4 py-3"><Badge className={`border-0 ${item.status==='approved'?'bg-green-500/10 text-green-400':item.status==='pending'?'bg-yellow-500/10 text-yellow-400':'bg-red-500/10 text-red-400'}`}>{item.status}</Badge></td>
                              </tr>
                            ))
                          }
                        </tbody>
                      </table>
                    </div>
                  </Card>
                </TabsContent>

                <TabsContent value="schools">
                  <Card className="bg-zinc-900 border-zinc-800 overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="w-full" data-testid="cat-schools-table">
                        <thead className="bg-zinc-950 border-b border-zinc-800">
                          <tr>{['Name','Type','Province','Town','Contact','Players','Teams'].map(h => <th key={h} className="px-4 py-3 text-left text-xs font-heading font-bold text-zinc-400 uppercase">{h}</th>)}</tr>
                        </thead>
                        <tbody>
                          {categoryInstitutions.length === 0 ? <tr><td colSpan={7} className="px-4 py-10 text-center text-zinc-500">No institutions found</td></tr> :
                            categoryInstitutions.map(item => (
                              <tr key={item.institution?.id} className="border-b border-zinc-800 hover:bg-zinc-800/30">
                                <td className="px-4 py-3 text-sm text-white font-semibold">{item.institution?.name}</td>
                                <td className="px-4 py-3 text-sm text-zinc-400 capitalize">{item.institution?.institution_type}</td>
                                <td className="px-4 py-3 text-sm text-zinc-400">{item.institution?.province}</td>
                                <td className="px-4 py-3 text-sm text-zinc-400">{item.institution?.town}</td>
                                <td className="px-4 py-3 text-sm text-zinc-400">{item.institution?.contact_email || '-'}</td>
                                <td className="px-4 py-3 text-sm font-mono font-bold">{item.player_count}</td>
                                <td className="px-4 py-3 text-sm font-mono font-bold">{item.team_count}</td>
                              </tr>
                            ))
                          }
                        </tbody>
                      </table>
                    </div>
                  </Card>
                </TabsContent>

                <TabsContent value="clubs">
                  <Card className="bg-zinc-900 border-zinc-800 overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="w-full" data-testid="cat-clubs-table">
                        <thead className="bg-zinc-950 border-b border-zinc-800">
                          <tr>{['Name','Type','Province','Town','Contact','Players','Teams'].map(h => <th key={h} className="px-4 py-3 text-left text-xs font-heading font-bold text-zinc-400 uppercase">{h}</th>)}</tr>
                        </thead>
                        <tbody>
                          {categoryInstitutions.length === 0 ? <tr><td colSpan={7} className="px-4 py-10 text-center text-zinc-500">No clubs found</td></tr> :
                            categoryInstitutions.map(item => (
                              <tr key={item.institution?.id} className="border-b border-zinc-800 hover:bg-zinc-800/30">
                                <td className="px-4 py-3 text-sm text-white font-semibold">{item.institution?.name}</td>
                                <td className="px-4 py-3 text-sm text-zinc-400 capitalize">{item.institution?.institution_type}</td>
                                <td className="px-4 py-3 text-sm text-zinc-400">{item.institution?.province}</td>
                                <td className="px-4 py-3 text-sm text-zinc-400">{item.institution?.town}</td>
                                <td className="px-4 py-3 text-sm text-zinc-400">{item.institution?.contact_email || '-'}</td>
                                <td className="px-4 py-3 text-sm font-mono font-bold">{item.player_count}</td>
                                <td className="px-4 py-3 text-sm font-mono font-bold">{item.team_count}</td>
                              </tr>
                            ))
                          }
                        </tbody>
                      </table>
                    </div>
                  </Card>
                </TabsContent>
              </Tabs>
            </TabsContent>
          )}

          {/* ANALYTICS/CHARTS TAB (SuperAdmin) */}
          {isSuperAdmin && (
            <TabsContent value="charts" data-testid="charts-content">
              <div className="space-y-6">
                {/* Row 1: Role distribution + Province */}
                <div className="grid md:grid-cols-2 gap-6">
                  <Card className="bg-zinc-900 border-zinc-800 p-6" data-testid="role-distribution-chart">
                    <h3 className="font-heading font-bold mb-4">User Distribution by Role</h3>
                    <ResponsiveContainer width="100%" height={280}>
                      <PieChart>
                        <Pie data={roleChartData} cx="50%" cy="50%" outerRadius={100} dataKey="value" label={({name, value}) => `${name}: ${value}`}>
                          {roleChartData.map((entry, i) => <Cell key={entry.name || i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                        </Pie>
                        <Tooltip contentStyle={{ background: '#18181b', border: '1px solid #3f3f46', borderRadius: '8px' }} />
                      </PieChart>
                    </ResponsiveContainer>
                  </Card>
                  <Card className="bg-zinc-900 border-zinc-800 p-6" data-testid="province-chart">
                    <h3 className="font-heading font-bold mb-4">Players by Province</h3>
                    <ResponsiveContainer width="100%" height={280}>
                      <BarChart data={provinceChartData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#3f3f46" />
                        <XAxis dataKey="province" tick={{ fill: '#a1a1aa', fontSize: 11 }} angle={-30} textAnchor="end" height={60} />
                        <YAxis tick={{ fill: '#a1a1aa', fontSize: 11 }} />
                        <Tooltip contentStyle={{ background: '#18181b', border: '1px solid #3f3f46', borderRadius: '8px' }} />
                        <Bar dataKey="count" fill="#22c55e" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </Card>
                </div>
                {/* Row 2: Position + Age */}
                <div className="grid md:grid-cols-2 gap-6">
                  <Card className="bg-zinc-900 border-zinc-800 p-6" data-testid="position-chart">
                    <h3 className="font-heading font-bold mb-4">Players by Position</h3>
                    <ResponsiveContainer width="100%" height={280}>
                      <BarChart data={positionChartData} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" stroke="#3f3f46" />
                        <XAxis type="number" tick={{ fill: '#a1a1aa', fontSize: 11 }} />
                        <YAxis type="category" dataKey="position" width={100} tick={{ fill: '#a1a1aa', fontSize: 11 }} />
                        <Tooltip contentStyle={{ background: '#18181b', border: '1px solid #3f3f46', borderRadius: '8px' }} />
                        <Bar dataKey="count" fill="#3b82f6" radius={[0, 4, 4, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </Card>
                  <Card className="bg-zinc-900 border-zinc-800 p-6" data-testid="age-chart">
                    <h3 className="font-heading font-bold mb-4">Age Distribution</h3>
                    <ResponsiveContainer width="100%" height={280}>
                      <BarChart data={ageChartData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#3f3f46" />
                        <XAxis dataKey="age" tick={{ fill: '#a1a1aa', fontSize: 11 }} />
                        <YAxis tick={{ fill: '#a1a1aa', fontSize: 11 }} />
                        <Tooltip contentStyle={{ background: '#18181b', border: '1px solid #3f3f46', borderRadius: '8px' }} />
                        <Bar dataKey="count" fill="#f59e0b" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </Card>
                </div>
                {/* Row 3: Content Analytics + Scout Activity */}
                {analyticsContent && (
                  <div className="grid md:grid-cols-2 gap-6">
                    <Card className="bg-zinc-900 border-zinc-800 p-6" data-testid="completion-chart">
                      <h3 className="font-heading font-bold mb-4">Profile Completion Distribution</h3>
                      <ResponsiveContainer width="100%" height={280}>
                        <PieChart>
                          <Pie data={analyticsContent.completion_distribution} cx="50%" cy="50%" outerRadius={100} dataKey="count" label={({range, count}) => `${range}%: ${count}`}>
                            {analyticsContent.completion_distribution.map((entry, i) => <Cell key={entry.name || entry.bucket || i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                          </Pie>
                          <Tooltip contentStyle={{ background: '#18181b', border: '1px solid #3f3f46', borderRadius: '8px' }} />
                        </PieChart>
                      </ResponsiveContainer>
                    </Card>
                    <Card className="bg-zinc-900 border-zinc-800 p-6" data-testid="verification-chart">
                      <h3 className="font-heading font-bold mb-4">Verification Status</h3>
                      <ResponsiveContainer width="100%" height={280}>
                        <PieChart>
                          <Pie data={[{name: 'Verified', value: analyticsContent.verification.verified}, {name: 'Unverified', value: analyticsContent.verification.unverified}]} cx="50%" cy="50%" outerRadius={100} dataKey="value" label={({name, value}) => `${name}: ${value}`}>
                            <Cell fill="#22c55e" /><Cell fill="#71717a" />
                          </Pie>
                          <Tooltip contentStyle={{ background: '#18181b', border: '1px solid #3f3f46', borderRadius: '8px' }} />
                        </PieChart>
                      </ResponsiveContainer>
                    </Card>
                  </div>
                )}
                {analyticsScout && (
                  <div className="grid md:grid-cols-2 gap-6">
                    <Card className="bg-zinc-900 border-zinc-800 p-6" data-testid="scout-position-chart">
                      <h3 className="font-heading font-bold mb-4">Most Scouted Positions</h3>
                      <ResponsiveContainer width="100%" height={280}>
                        <BarChart data={analyticsScout.most_scouted_positions} layout="vertical">
                          <CartesianGrid strokeDasharray="3 3" stroke="#3f3f46" />
                          <XAxis type="number" tick={{ fill: '#a1a1aa', fontSize: 11 }} />
                          <YAxis type="category" dataKey="position" width={100} tick={{ fill: '#a1a1aa', fontSize: 11 }} />
                          <Tooltip contentStyle={{ background: '#18181b', border: '1px solid #3f3f46', borderRadius: '8px' }} />
                          <Bar dataKey="count" fill="#8b5cf6" radius={[0, 4, 4, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </Card>
                    <Card className="bg-zinc-900 border-zinc-800 p-6" data-testid="scout-province-chart">
                      <h3 className="font-heading font-bold mb-4">Scouting Activity by Province</h3>
                      <ResponsiveContainer width="100%" height={280}>
                        <BarChart data={analyticsScout.scouting_by_province}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#3f3f46" />
                          <XAxis dataKey="province" tick={{ fill: '#a1a1aa', fontSize: 11 }} angle={-30} textAnchor="end" height={60} />
                          <YAxis tick={{ fill: '#a1a1aa', fontSize: 11 }} />
                          <Tooltip contentStyle={{ background: '#18181b', border: '1px solid #3f3f46', borderRadius: '8px' }} />
                          <Bar dataKey="count" fill="#ec4899" radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </Card>
                  </div>
                )}
                {/* Growth trend */}
                {analyticsGrowth?.daily_registrations?.length > 0 && (
                  <Card className="bg-zinc-900 border-zinc-800 p-6" data-testid="growth-chart">
                    <h3 className="font-heading font-bold mb-4">Registration Trend (Last 30 Days)</h3>
                    <ResponsiveContainer width="100%" height={300}>
                      <LineChart data={analyticsGrowth.daily_registrations}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#3f3f46" />
                        <XAxis dataKey="date" tick={{ fill: '#a1a1aa', fontSize: 10 }} />
                        <YAxis tick={{ fill: '#a1a1aa', fontSize: 11 }} />
                        <Tooltip contentStyle={{ background: '#18181b', border: '1px solid #3f3f46', borderRadius: '8px' }} />
                        <Legend />
                        <Line type="monotone" dataKey="total" stroke="#22c55e" strokeWidth={2} dot={false} />
                        <Line type="monotone" dataKey="players" stroke="#3b82f6" strokeWidth={1} dot={false} />
                        <Line type="monotone" dataKey="scouts" stroke="#8b5cf6" strokeWidth={1} dot={false} />
                      </LineChart>
                    </ResponsiveContainer>
                  </Card>
                )}

                {/* Messaging Analytics — Section 30.6 */}
                {analyticsMessaging && (
                  <div className="space-y-4" data-testid="messaging-analytics">
                    <h3 className="text-xl font-heading font-bold flex items-center gap-2 mt-4"><MessageSquare className="w-5 h-5 text-primary-neon" />Messaging Activity</h3>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                      <StatCard icon={MessageSquare} label="Total Messages" value={analyticsMessaging.total_messages} />
                      <StatCard icon={Activity} label="Active Threads" value={analyticsMessaging.active_threads} color="text-green-400" bgColor="bg-green-500/10" />
                      <StatCard icon={FolderOpen} label="Archived" value={analyticsMessaging.archived_threads} color="text-zinc-400" bgColor="bg-zinc-700/30" />
                      <StatCard icon={Clock} label="Unread" value={analyticsMessaging.unread_messages} color="text-yellow-400" bgColor="bg-yellow-500/10" />
                      <StatCard icon={Users} label="Total Threads" value={analyticsMessaging.total_threads} color="text-blue-400" bgColor="bg-blue-500/10" />
                    </div>
                    <div className="grid md:grid-cols-2 gap-6">
                      {analyticsMessaging.messages_by_role?.length > 0 && (
                        <Card className="bg-zinc-900 border-zinc-800 p-6" data-testid="messages-by-role-chart">
                          <h4 className="font-heading font-bold mb-4">Messages by Sender Role</h4>
                          <ResponsiveContainer width="100%" height={240}>
                            <PieChart>
                              <Pie data={analyticsMessaging.messages_by_role} cx="50%" cy="50%" outerRadius={90} dataKey="count" label={({ role, count }) => `${role}: ${count}`}>
                                {analyticsMessaging.messages_by_role.map((entry, i) => <Cell key={entry.name || entry.role || i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                              </Pie>
                              <Tooltip contentStyle={{ background: '#18181b', border: '1px solid #3f3f46', borderRadius: '8px' }} />
                            </PieChart>
                          </ResponsiveContainer>
                        </Card>
                      )}
                      {analyticsMessaging.daily_messages?.length > 0 && (
                        <Card className="bg-zinc-900 border-zinc-800 p-6" data-testid="daily-messages-chart">
                          <h4 className="font-heading font-bold mb-4">Daily Messages (Last 30 Days)</h4>
                          <ResponsiveContainer width="100%" height={240}>
                            <LineChart data={analyticsMessaging.daily_messages}>
                              <CartesianGrid strokeDasharray="3 3" stroke="#3f3f46" />
                              <XAxis dataKey="date" tick={{ fill: '#a1a1aa', fontSize: 10 }} />
                              <YAxis tick={{ fill: '#a1a1aa', fontSize: 11 }} />
                              <Tooltip contentStyle={{ background: '#18181b', border: '1px solid #3f3f46', borderRadius: '8px' }} />
                              <Line type="monotone" dataKey="count" stroke="#06b6d4" strokeWidth={2} dot={false} />
                            </LineChart>
                          </ResponsiveContainer>
                        </Card>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </TabsContent>
          )}

          {/* FINANCE TAB (SuperAdmin) — Spec Section 27 */}
          {isSuperAdmin && (
            <TabsContent value="finance" data-testid="finance-content">
              <div className="space-y-6">
                <div className="flex justify-between items-center">
                  <h2 className="text-2xl font-heading font-bold flex items-center gap-2"><Wallet className="w-5 h-5 text-primary-neon" />Finance Overview</h2>
                  <Button onClick={() => setShowCreateTxn(!showCreateTxn)} className="btn-primary" data-testid="toggle-create-txn-button">
                    <Plus className="w-4 h-4 mr-1" />{showCreateTxn ? 'Cancel' : 'Record Transaction'}
                  </Button>
                </div>

                {financeOverview && (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="finance-stats">
                    <StatCard icon={DollarSign} label="Total Revenue" value={`R${financeOverview.total_revenue.toLocaleString()}`} color="text-green-400" bgColor="bg-green-500/10" />
                    <StatCard icon={TrendingDown} label="Total Expenses" value={`R${financeOverview.total_expenses.toLocaleString()}`} color="text-red-400" bgColor="bg-red-500/10" />
                    <StatCard icon={TrendingUp} label="Net Income" value={`R${financeOverview.net_income.toLocaleString()}`} color={financeOverview.net_income >= 0 ? 'text-green-400' : 'text-red-400'} bgColor={financeOverview.net_income >= 0 ? 'bg-green-500/10' : 'bg-red-500/10'} />
                    <StatCard icon={Activity} label="MRR (current month)" value={`R${financeOverview.mrr.toLocaleString()}`} color="text-blue-400" bgColor="bg-blue-500/10" />
                  </div>
                )}

                {financeOverview?.billing && (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="payfast-billing-stats">
                    <StatCard icon={Wallet} label="PayFast Revenue (live)" value={`R${financeOverview.billing.payfast_revenue.toLocaleString()}`} color="text-primary-neon" bgColor="bg-primary-neon/10" />
                    <StatCard icon={Activity} label="Live Billing MRR" value={`R${financeOverview.billing.live_mrr.toLocaleString()}`} color="text-primary-neon" bgColor="bg-primary-neon/10" />
                    <StatCard icon={Users} label="Scout Subscriptions" value={financeOverview.billing.active_scout_subscriptions} color="text-blue-400" bgColor="bg-blue-500/10" />
                    <StatCard icon={Users} label="Institution Subscriptions" value={financeOverview.billing.active_institution_subscriptions} color="text-blue-400" bgColor="bg-blue-500/10" />
                  </div>
                )}

                {financeOverview && (
                  <Card className="bg-zinc-900 border-zinc-800 p-5" data-testid="vat-progress">
                    <div className="flex justify-between items-center mb-2">
                      <h3 className="font-heading font-bold text-sm">VAT Registration Threshold (ZAR 1,000,000)</h3>
                      <span className="text-sm text-zinc-400">R{financeOverview.annual_revenue.toLocaleString()} / R{financeOverview.vat_threshold.toLocaleString()} ({financeOverview.vat_progress}%)</span>
                    </div>
                    <div className="w-full bg-zinc-800 rounded-full h-3">
                      <div className={`h-3 rounded-full transition-all ${financeOverview.vat_progress >= 100 ? 'bg-red-500' : financeOverview.vat_progress >= 75 ? 'bg-yellow-500' : 'bg-primary-neon'}`} style={{ width: `${financeOverview.vat_progress}%` }} />
                    </div>
                  </Card>
                )}

                {showCreateTxn && (
                  <Card className="bg-zinc-900 border-zinc-800 p-6" data-testid="create-txn-form">
                    <h3 className="text-lg font-heading font-bold mb-4">Record Transaction</h3>
                    <form onSubmit={handleCreateTransaction} className="space-y-4">
                      <div className="grid md:grid-cols-2 gap-4">
                        <div><Label className="text-zinc-300">Type</Label>
                          <Select value={newTxn.type} onValueChange={(v) => setNewTxn({...newTxn, type: v})}>
                            <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white mt-1" data-testid="txn-type-select"><SelectValue /></SelectTrigger>
                            <SelectContent className="bg-zinc-900 border-zinc-800 text-white">
                              <SelectItem value="revenue">Revenue</SelectItem>
                              <SelectItem value="expense">Expense</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div><Label className="text-zinc-300">Amount (ZAR)</Label><Input type="number" step="0.01" value={newTxn.amount} onChange={(e) => setNewTxn({...newTxn, amount: e.target.value})} className="bg-zinc-800 border-zinc-700 text-white mt-1" required data-testid="txn-amount-input" /></div>
                        <div><Label className="text-zinc-300">Category</Label>
                          <Select value={newTxn.category} onValueChange={(v) => setNewTxn({...newTxn, category: v})}>
                            <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white mt-1" data-testid="txn-category-select"><SelectValue /></SelectTrigger>
                            <SelectContent className="bg-zinc-900 border-zinc-800 text-white">
                              <SelectItem value="subscription">Subscription</SelectItem>
                              <SelectItem value="advertising">Advertising</SelectItem>
                              <SelectItem value="sponsorship">Sponsorship</SelectItem>
                              <SelectItem value="infrastructure">Infrastructure</SelectItem>
                              <SelectItem value="marketing">Marketing</SelectItem>
                              <SelectItem value="payroll">Payroll</SelectItem>
                              <SelectItem value="other">Other</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="flex items-end gap-2">
                          <label className="flex items-center gap-2 text-sm text-zinc-300 cursor-pointer" data-testid="txn-recurring-toggle">
                            <input type="checkbox" checked={newTxn.recurring} onChange={(e) => setNewTxn({...newTxn, recurring: e.target.checked})} className="w-4 h-4 accent-primary-neon" />
                            Recurring (counts toward MRR)
                          </label>
                        </div>
                      </div>
                      <div><Label className="text-zinc-300">Description</Label><Input value={newTxn.description} onChange={(e) => setNewTxn({...newTxn, description: e.target.value})} className="bg-zinc-800 border-zinc-700 text-white mt-1" required data-testid="txn-description-input" /></div>
                      <Button type="submit" className="btn-primary" data-testid="submit-txn-button">Record</Button>
                    </form>
                  </Card>
                )}

                <div className="grid md:grid-cols-2 gap-6">
                  {financeOverview?.monthly_breakdown?.length > 0 && (
                    <Card className="bg-zinc-900 border-zinc-800 p-6" data-testid="monthly-finance-chart">
                      <h3 className="font-heading font-bold mb-4">Monthly Revenue vs Expenses</h3>
                      <ResponsiveContainer width="100%" height={260}>
                        <BarChart data={financeOverview.monthly_breakdown}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#3f3f46" />
                          <XAxis dataKey="month" tick={{ fill: '#a1a1aa', fontSize: 11 }} />
                          <YAxis tick={{ fill: '#a1a1aa', fontSize: 11 }} />
                          <Tooltip contentStyle={{ background: '#18181b', border: '1px solid #3f3f46', borderRadius: '8px' }} />
                          <Legend />
                          <Bar dataKey="revenue" fill="#22c55e" radius={[4, 4, 0, 0]} />
                          <Bar dataKey="expenses" fill="#ef4444" radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </Card>
                  )}
                  {financeOverview?.category_breakdown?.length > 0 && (
                    <Card className="bg-zinc-900 border-zinc-800 p-6" data-testid="category-finance-chart">
                      <h3 className="font-heading font-bold mb-4">By Category</h3>
                      <ResponsiveContainer width="100%" height={260}>
                        <PieChart>
                          <Pie data={financeOverview.category_breakdown} cx="50%" cy="50%" outerRadius={90} dataKey="amount" label={({ category, amount }) => `${category}: R${amount.toLocaleString()}`}>
                            {financeOverview.category_breakdown.map((entry, i) => <Cell key={entry.name || entry.category || i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                          </Pie>
                          <Tooltip contentStyle={{ background: '#18181b', border: '1px solid #3f3f46', borderRadius: '8px' }} />
                        </PieChart>
                      </ResponsiveContainer>
                    </Card>
                  )}
                </div>

                <Card className="bg-zinc-900 border-zinc-800 overflow-hidden" data-testid="transactions-table-card">
                  <div className="p-4 border-b border-zinc-800 flex justify-between items-center">
                    <h3 className="font-heading font-bold flex items-center gap-2"><Receipt className="w-4 h-4 text-primary-neon" />Recent Transactions</h3>
                    <Badge className="bg-zinc-800 text-zinc-400 border-0">{financeTxns.length}</Badge>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full" data-testid="transactions-table">
                      <thead className="bg-zinc-950 border-b border-zinc-800">
                        <tr>{['Date','Type','Category','Description','Amount','Recurring','Action'].map(h => <th key={h} className="px-4 py-3 text-left text-xs font-heading font-bold text-zinc-400 uppercase">{h}</th>)}</tr>
                      </thead>
                      <tbody>
                        {financeTxns.length === 0 ? <tr><td colSpan={7} className="px-4 py-10 text-center text-zinc-500">No transactions recorded yet</td></tr> :
                          financeTxns.map(t => (
                            <tr key={t.id} className="border-b border-zinc-800 hover:bg-zinc-800/30" data-testid={`txn-row-${t.id}`}>
                              <td className="px-4 py-3 text-xs text-zinc-500 font-mono">{t.date}</td>
                              <td className="px-4 py-3"><Badge className={t.type === 'revenue' ? 'bg-green-500/10 text-green-400 border-0' : 'bg-red-500/10 text-red-400 border-0'}>{t.type}</Badge></td>
                              <td className="px-4 py-3 text-sm text-zinc-300 capitalize">{t.category}</td>
                              <td className="px-4 py-3 text-sm text-zinc-400 max-w-xs truncate">{t.description}</td>
                              <td className={`px-4 py-3 text-sm font-mono font-bold ${t.type === 'revenue' ? 'text-green-400' : 'text-red-400'}`}>{t.type === 'revenue' ? '+' : '-'}R{t.amount?.toLocaleString()}</td>
                              <td className="px-4 py-3 text-xs">{t.recurring ? <Badge className="bg-blue-500/10 text-blue-400 border-0">Yes</Badge> : <span className="text-zinc-600">—</span>}</td>
                              <td className="px-4 py-3">
                                <Button size="sm" variant="ghost" onClick={() => handleDeleteTransaction(t.id)} className="text-red-400 hover:text-red-300 h-7" data-testid={`delete-txn-${t.id}`}><Trash2 className="w-3 h-3" /></Button>
                              </td>
                            </tr>
                          ))
                        }
                      </tbody>
                    </table>
                  </div>
                </Card>
              </div>
            </TabsContent>
          )}

          {/* MANAGE ADMINS TAB (SuperAdmin) */}
          {isSuperAdmin && (
            <TabsContent value="manage" data-testid="manage-admins-content">
              <div className="space-y-6">
                <div className="flex justify-between items-center">
                  <h2 className="text-2xl font-heading font-bold">Administrator Management</h2>
                  <Button onClick={() => setShowCreateAdmin(!showCreateAdmin)} className="btn-primary" data-testid="toggle-create-admin-button">
                    <UserPlus className="w-4 h-4 mr-2" />{showCreateAdmin ? 'Cancel' : 'Create Admin'}
                  </Button>
                </div>

                {showCreateAdmin && (
                  <Card className="bg-zinc-900 border-zinc-800 p-6" data-testid="create-admin-form">
                    <h3 className="text-lg font-heading font-bold mb-4">Create New Administrator</h3>
                    <form onSubmit={handleCreateAdmin} className="space-y-4">
                      <div className="grid md:grid-cols-2 gap-4">
                        <div><Label className="text-zinc-300">Full Name</Label><Input value={newAdmin.full_name} onChange={(e) => setNewAdmin({...newAdmin, full_name: e.target.value})} className="bg-zinc-800 border-zinc-700 text-white mt-1" required data-testid="new-admin-name-input" /></div>
                        <div><Label className="text-zinc-300">Email</Label><Input type="email" value={newAdmin.email} onChange={(e) => setNewAdmin({...newAdmin, email: e.target.value})} className="bg-zinc-800 border-zinc-700 text-white mt-1" required data-testid="new-admin-email-input" /></div>
                        <div>
                          <Label className="text-zinc-300">Password</Label>
                          <div className="flex gap-2 mt-1">
                            <Input type="text" value={newAdmin.password} onChange={(e) => setNewAdmin({...newAdmin, password: e.target.value})} className="bg-zinc-800 border-zinc-700 text-white" required data-testid="new-admin-password-input" />
                            <Button type="button" onClick={handleGeneratePassword} variant="outline" className="border-zinc-700 text-zinc-400 hover:text-white text-xs flex-shrink-0" data-testid="generate-password-button"><KeyRound className="w-3 h-3 mr-1" />Generate</Button>
                          </div>
                        </div>
                        <div>
                          <Label className="text-zinc-300">Access Role</Label>
                          <Select value={newAdmin.admin_role} onValueChange={(v) => setNewAdmin({...newAdmin, admin_role: v})}>
                            <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white mt-1" data-testid="admin-role-select"><SelectValue /></SelectTrigger>
                            <SelectContent className="bg-zinc-900 border-zinc-800 text-white">
                              {Object.entries(ADMIN_ROLE_LABELS).map(([k,v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <Button type="submit" className="btn-primary" disabled={creatingAdmin} data-testid="create-admin-submit-button">{creatingAdmin ? 'Creating...' : 'Create Admin'}</Button>
                    </form>
                  </Card>
                )}

                <Card className="bg-zinc-900 border-zinc-800 overflow-hidden">
                  <div className="p-4 border-b border-zinc-800"><h3 className="font-heading font-bold">Current Administrators</h3></div>
                  <div className="overflow-x-auto">
                    <table className="w-full" data-testid="admins-table">
                      <thead className="bg-zinc-950 border-b border-zinc-800">
                        <tr>{['Name','Email','System Role','Access Role','Created','Actions'].map(h => <th key={h} className="px-4 py-3 text-left text-xs font-heading font-bold text-zinc-400 uppercase">{h}</th>)}</tr>
                      </thead>
                      <tbody>
                        {allAdmins.map(admin => (
                          <tr key={admin.id} className="border-b border-zinc-800 hover:bg-zinc-800/50">
                            <td className="px-4 py-3 text-sm text-white">{admin.full_name}</td>
                            <td className="px-4 py-3 text-sm text-zinc-400">{admin.email}</td>
                            <td className="px-4 py-3"><Badge className={admin.role==='superadmin'?'bg-yellow-500/10 text-yellow-400 border border-yellow-500/20':'bg-blue-500/10 text-blue-400 border border-blue-500/20'}>{admin.role==='superadmin'?'Super Admin':'Admin'}</Badge></td>
                            <td className="px-4 py-3 text-sm text-zinc-300">{ADMIN_ROLE_LABELS[admin.admin_role] || 'Full Admin'}</td>
                            <td className="px-4 py-3 text-sm text-zinc-500">{admin.created_at ? new Date(admin.created_at).toLocaleDateString() : '-'}</td>
                            <td className="px-4 py-3">
                              {admin.role !== 'superadmin' && (
                                <Button size="sm" variant="ghost" onClick={() => handleDeleteAdmin(admin.id)} className="text-red-400 hover:text-red-300 h-7" data-testid={`delete-admin-${admin.id}`}><Trash2 className="w-3 h-3" /></Button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </Card>
              </div>
            </TabsContent>
          )}

          {/* AUDIT LOG TAB (SuperAdmin) */}
          {isSuperAdmin && (
            <TabsContent value="audit" data-testid="audit-content">
              <Card className="bg-zinc-900 border-zinc-800 overflow-hidden">
                <div className="p-4 border-b border-zinc-800 flex justify-between items-center">
                  <h3 className="font-heading font-bold flex items-center gap-2"><History className="w-4 h-4 text-primary-neon" />Audit Trail</h3>
                  <Badge className="bg-zinc-800 text-zinc-400 border-0">{auditLogs.length} entries</Badge>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full" data-testid="audit-table">
                    <thead className="bg-zinc-950 border-b border-zinc-800">
                      <tr>{['Timestamp','Actor','Action','Target','Details'].map(h => <th key={h} className="px-4 py-3 text-left text-xs font-heading font-bold text-zinc-400 uppercase">{h}</th>)}</tr>
                    </thead>
                    <tbody>
                      {auditLogs.length === 0 ? <tr><td colSpan={5} className="px-4 py-10 text-center text-zinc-500">No audit entries yet</td></tr> :
                        auditLogs.map(log => (
                          <tr key={log.id} className="border-b border-zinc-800 hover:bg-zinc-800/30">
                            <td className="px-4 py-3 text-xs text-zinc-500 font-mono">{new Date(log.timestamp).toLocaleString()}</td>
                            <td className="px-4 py-3 text-sm text-white">{log.actor_name || log.actor_email || '-'}</td>
                            <td className="px-4 py-3"><Badge className="bg-primary-neon/10 text-primary-neon border-0 text-xs">{log.action?.replace('_',' ')}</Badge></td>
                            <td className="px-4 py-3 text-sm text-zinc-400">{log.target_type}</td>
                            <td className="px-4 py-3 text-xs text-zinc-500 max-w-xs truncate">{log.details}</td>
                          </tr>
                        ))
                      }
                    </tbody>
                  </table>
                </div>
              </Card>
            </TabsContent>
          )}
        </Tabs>
      </div>
      <XVAssistWidget />
    </div>
  );
}
